/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * The PC drawer (docs/CONTRACT-fleet-governance.md section 12.2), from GET /v1/admin/fleet/devices/:deviceId, top to
 * bottom: identity and keys; status and version; four 30-day sparklines; switches; updates with the OTA gate's reasons as
 * sentences; health with its note in words; local connections; the last 120 log entries (the existing
 * GET /v1/admin/desktop/logs?device=) with a category filter and Copy. Links out to Support, PC switches and the OTA view.
 */
import { useState, type ReactNode } from 'react';
import { Copy, FileText } from 'lucide-react';
import { describeDeviceHealthNote } from '../../lib/deviceHealth';
import {
  ELIGIBILITY_SENTENCES, LOG_CATEGORIES, VERSION_SOURCE_LABELS, ageText, eligibilitySentence, fleetApi, fleetFailure, keyShort, logCategoryOptions,
  logsText, mcpChip, mcpName, problemChip, ringLabel, sourcesFailedText, statusChip, switchRows,
  type DesktopLogEntry, type FleetDeviceAnswer, type FleetKey, type FleetMcpServer,
} from '../../lib/fleet';
import { statusOf } from '../../lib/fleetHttp';
import { SaveNote } from '../../components/SaveNote';
import { Button, Drawer, EmptyState, LinkButton, Select, Skeleton, Sparkline, StatusChip, Table, Tag, type Column } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { linkClick } from '../../shell/router';
import { useLoad } from '../pulse/common';
import { Chip, Facts, LoadError, SourcesNote } from './parts';

const PURPOSE_LABELS: Record<FleetKey['purpose'], string> = { user: 'Person', 'broker-audit': 'Broker audit' };
const LEVEL_TONES: Record<DesktopLogEntry['level'], 'warn' | 'bad'> = { warn: 'warn', error: 'bad', critical: 'bad' };

/** Writes text to the clipboard; false where the browser refuses. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function PcDrawer({ deviceId, onClose }: { deviceId: string | null; onClose: () => void }) {
  const { load, reload, reloading } = useLoad<FleetDeviceAnswer | null>(() => (deviceId ? fleetApi.device(deviceId) : Promise.resolve(null)), `device|${deviceId ?? ''}`);
  const answer = load.state === 'ready' ? load.data : null;
  const record = answer?.record ?? null;
  const [copied, setCopied] = useState<{ ok: boolean; text: string } | null>(null);

  const copyId = async () => {
    if (!deviceId) return;
    setCopied((await copyText(deviceId)) ? { ok: true, text: 'Device id copied.' } : { ok: false, text: 'The browser did not allow copying; select the id instead.' });
  };

  return (
    <Drawer
      open={deviceId !== null}
      onClose={onClose}
      title={record ? record.computerName ?? 'Unnamed PC' : 'PC'}
      description={record ? `${record.owner?.email ?? 'No owner known'} · ${statusChip(record.status).label}` : deviceId ? 'Loading…' : undefined}
      className="ui-drawer--wide"
      footer={(
        <span className="ui-actions">
          <LinkButton size="sm" href="/support?view=devices" onClick={(event) => linkClick(event)}>Open in Support</LinkButton>
          <LinkButton size="sm" href="/pc-switches" onClick={(event) => linkClick(event)}>Open in PC switches</LinkButton>
          <LinkButton size="sm" href="/support?view=ota" onClick={(event) => linkClick(event)}>Open the OTA view</LinkButton>
        </span>
      )}
    >
      {load.state === 'loading' && deviceId ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div> : null}
      {load.state === 'failed' ? (
        statusOf(load.error) === 404 && (load.error as { message?: unknown }).message === 'unknown_device'
          ? <EmptyState compact title="Fleet health knows no PC with this id">It may have been swept after its retention, or the link names another service's PC.</EmptyState>
          : <LoadError title="The PC could not be loaded" error={load.error} words={(error) => fleetFailure(error)} onRetry={reload} retrying={reloading} />
      ) : null}
      {answer && record ? (
        <div className="ui-form">
          <SourcesNote text={sourcesFailedText(answer.sourcesFailed)} />
          <Identity answer={answer} onCopyId={() => void copyId()} />
          <SaveNote note={copied} />
          <StatusBlock answer={answer} />
          <ThirtyDays answer={answer} />
          <SwitchesBlock answer={answer} />
          <Updates answer={answer} />
          <Health answer={answer} />
          <LocalConnections servers={record.mcp?.servers ?? null} at={record.mcp?.at ?? null} />
          <LogEntries deviceId={record.deviceId} />
        </div>
      ) : null}
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="ui-section">
      <h3 className="ui-section-title">{title}</h3>
      {children}
    </div>
  );
}

function Identity({ answer, onCopyId }: { answer: FleetDeviceAnswer; onCopyId: () => void }) {
  const record = answer.record;
  const keyColumns: Array<Column<FleetKey>> = [
    { key: 'fingerprint', header: 'Key', mono: true, cell: (key) => <span title={key.fingerprint}>{keyShort(key.fingerprint)}</span> },
    { key: 'purpose', header: 'Purpose', cell: (key) => PURPOSE_LABELS[key.purpose] ?? key.purpose },
    { key: 'status', header: 'Status', cell: (key) => (key.status === 'active' ? <StatusChip tone="ok">Active</StatusChip> : <StatusChip tone="neutral" plain>Revoked</StatusChip>) },
    { key: 'owner', header: 'Owner', hideOnPhone: true, cell: (key) => key.owner?.email ?? '—' },
    { key: 'enrolled', header: 'Enrolled', muted: true, cell: (key) => dateTime(key.enrolledAt) },
    { key: 'lastEnrolled', header: 'Last enrolled', muted: true, hideOnPhone: true, cell: (key) => dateTime(key.lastEnrolledAt) },
  ];
  return (
    <Section title="Identity">
      <Facts label="Identity" rows={[
        ['Computer name', record.computerName ?? 'Not reported'],
        ['Device id', <span className="ui-chips"><code>{record.deviceId}</code><Button variant="ghost" size="sm" icon={Copy} onClick={onCopyId}>Copy</Button></span>],
        ['Owner', record.owner?.email ?? 'No owner known'],
        ['Enrolled', dateTime(record.enrolledAt)],
        ['Last enrolled', dateTime(record.lastEnrolledAt)],
      ]} />
      {record.keys.length ? (
        <div className="ui-table-box">
          <Table stacked label="Keys" columns={keyColumns} rows={record.keys} rowKey={(key) => key.fingerprint} stickyHeader={false} />
        </div>
      ) : <p className="ui-para ui-quiet">The PC registry holds no key for this PC.</p>}
    </Section>
  );
}

function StatusBlock({ answer }: { answer: FleetDeviceAnswer }) {
  const record = answer.record;
  const disagrees = record.problems.includes('version-disagrees');
  const versionLine = record.agentVersion
    ? `${record.agentVersion} · from the ${record.versionSource ? VERSION_SOURCE_LABELS[record.versionSource] : 'reports'} at ${dateTime(record.versionSeenAt)}`
    : 'Not reported';
  const ringLine = record.ring
    ? `${ringLabel(record.ring)} · from the ${record.ringSource === 'switches' ? 'PC switches report' : 'health report'}${record.switchesRing && record.switchesRing.ring !== record.ring ? `; PC switches say ${ringLabel(record.switchesRing.ring)}` : ''}`
    : 'Unknown';
  const rows: Array<[ReactNode, ReactNode]> = [
    ['Status', <span className="ui-chips"><Chip spec={statusChip(record.status)} /><span className="ui-quiet">Last seen {ageText(record.lastSeenAt)}</span></span>],
    ['Version', versionLine],
    ['Ring', ringLine],
  ];
  if (disagrees) {
    rows.push(['Every report', (
      <ul className="ui-list" aria-label="Versions by source">
        {record.versions.map((item, index) => <li key={`${item.source}|${index}`}>{`${item.version} · ${VERSION_SOURCE_LABELS[item.source] ?? item.source} · ${dateTime(item.at)}`}</li>)}
      </ul>
    )]);
  }
  return (
    <Section title="Status">
      <Facts label="Status" rows={rows} />
      {record.problems.length ? <span className="ui-chips" aria-label="Problem kinds">{record.problems.map((kind) => <Chip key={kind} spec={problemChip(kind, record)} />)}</span> : null}
    </Section>
  );
}

function ThirtyDays({ answer }: { answer: FleetDeviceAnswer }) {
  const days = answer.days;
  const series: Array<{ key: 'errors' | 'crashes' | 'pushDrops' | 'signInFailures'; label: string }> = [
    { key: 'errors', label: 'Errors' }, { key: 'crashes', label: 'Crashes' }, { key: 'pushDrops', label: 'Push drops' }, { key: 'signInFailures', label: 'Sign-in failures' },
  ];
  return (
    <Section title="30 days">
      <Facts label="30 days" rows={series.map(({ key, label }) => {
        const values = days.map((day) => day[key] ?? 0);
        const total = values.reduce((sum, value) => sum + value, 0);
        return [label, <span className="ui-chips"><Sparkline values={values} label={`${label} per day, last ${values.length} days`} tone={key === 'errors' ? 'data' : total > 0 ? 'bad' : 'data'} /><span className="ui-quiet">{count(total)} in 30 days</span></span>];
      })} />
    </Section>
  );
}

function SwitchesBlock({ answer }: { answer: FleetDeviceAnswer }) {
  const switches = answer.record.switches;
  if (!switches) return <Section title="Switches"><p className="ui-para ui-quiet">Not reported</p></Section>;
  const rows = switchRows(switches);
  const columns: Array<Column<(typeof rows)[number]>> = [
    { key: 'label', header: 'Switch', cell: (row) => <span title={row.key}>{row.label}</span> },
    { key: 'value', header: 'Value', cell: (row) => row.value },
    { key: 'source', header: 'Set by', muted: true, cell: (row) => row.source },
    { key: 'differs', header: <span className="ui-sr">Differs from everyone</span>, cell: (row) => (row.differs ? <StatusChip tone="info" plain>Differs from everyone</StatusChip> : null) },
  ];
  return (
    <Section title="Switches">
      <Facts label="Switches" rows={[
        ['Revision', `${count(switches.revisionApplied)} applied of ${count(switches.revisionCurrent)}`],
        ['Reported', dateTime(switches.reportedAt)],
      ]} />
      <div className="ui-table-box">
        <Table stacked label="Switches" columns={columns} rows={rows} rowKey={(row) => row.key} stickyHeader={false} empty={<p className="ui-para ui-quiet">The report holds no switch values.</p>} />
      </div>
    </Section>
  );
}

function Updates({ answer }: { answer: FleetDeviceAnswer }) {
  const ota = answer.record.ota;
  const refused = answer.record.push.lastRefused;
  const eligibility = ota.eligibility === null
    ? "Its ring has no current version, so there is nothing to check."
    : ota.eligibility.length === 0
      ? "Eligible for its ring's current release."
      : <ul className="ui-list" aria-label="Why an update would be refused">{ota.eligibility.map((reason) => <li key={reason}>{eligibilitySentence(reason)}</li>)}</ul>;
  return (
    <Section title="Updates">
      <Facts label="Updates" rows={[
        ['Last offered', ota.lastOffered ? `${ota.lastOffered.version} · ${dateTime(ota.lastOffered.at)}${ota.lastOffered.rollback ? ' · rollback' : ''}` : 'None offered'],
        ['Applied', ota.lastAck ? (ota.lastAck.applied ? `Yes${ota.lastAck.at ? `, seen ${dateTime(ota.lastAck.at)}` : ''}` : 'Not yet') : '—'],
        ['Eligibility', eligibility],
        ['Last refusal', refused ? `${refused.type}: ${refused.reason} · ${dateTime(refused.at)}` : 'None'],
      ]} />
    </Section>
  );
}

function Health({ answer }: { answer: FleetDeviceAnswer }) {
  const health = answer.record.health;
  if (!health) return <Section title="Health"><p className="ui-para ui-quiet">{ELIGIBILITY_SENTENCES.health_missing}</p></Section>;
  const note = describeDeviceHealthNote(health.lastError);
  return (
    <Section title="Health">
      <Facts label="Health" rows={[
        ['Last report', dateTime(health.at)],
        ['Healthy', health.healthy ? <StatusChip tone="ok">Healthy</StatusChip> : <StatusChip tone="bad">Unhealthy</StatusChip>],
        ['Crash count', health.crashCount === null ? 'Not reported' : count(health.crashCount)],
        ['Note', note ? <span className={note.tone === 'bad' ? 'ui-text-bad' : note.tone === 'warn' ? 'ui-text-warn' : undefined}>{note.text}</span> : 'None'],
      ]} />
    </Section>
  );
}

function LocalConnections({ servers, at }: { servers: FleetMcpServer[] | null; at: number | null }) {
  if (servers === null) return <Section title="Local connections"><p className="ui-para ui-quiet">Not reported by this agent version</p></Section>;
  const columns: Array<Column<FleetMcpServer>> = [
    { key: 'server', header: 'Server', cell: (row) => <span title={row.server}>{mcpName(row)}</span> },
    { key: 'status', header: 'Status', cell: (row) => <Chip spec={mcpChip(row.status)} /> },
    { key: 'errorCode', header: 'Error code', mono: true, cell: (row) => row.errorCode ?? '—' },
    { key: 'uptime', header: 'Success 24 h', numeric: true, cell: (row) => (row.uptimePercent24h === null ? '—' : `${row.uptimePercent24h.toFixed(0)} %`) },
    { key: 'tools', header: 'Tools', numeric: true, cell: (row) => count(row.toolCount) },
  ];
  return (
    <Section title="Local connections">
      {at ? <p className="ui-para ui-quiet">Reported {dateTime(at)}</p> : null}
      <div className="ui-table-box">
        <Table stacked label="Local connections" columns={columns} rows={servers} rowKey={(row) => row.server} stickyHeader={false} empty={<p className="ui-para ui-quiet">No local connections on this PC.</p>} />
      </div>
    </Section>
  );
}

function LogEntries({ deviceId }: { deviceId: string }) {
  const { load, reload, reloading } = useLoad(() => fleetApi.deviceLogs(deviceId), `logs|${deviceId}`);
  const [category, setCategory] = useState('');
  const [copied, setCopied] = useState<{ ok: boolean; text: string } | null>(null);
  const missing = load.state === 'failed' && statusOf(load.error) === 404;
  const entries = load.state === 'ready' ? [...load.data.entries].reverse() : [];
  const shown = category ? entries.filter((entry) => entry.category === category) : entries;
  const columns: Array<Column<DesktopLogEntry>> = [
    { key: 'at', header: 'Time', muted: true, cell: (entry) => dateTime(entry.atMs) },
    { key: 'level', header: 'Level', cell: (entry) => <StatusChip tone={LEVEL_TONES[entry.level] ?? 'warn'}>{entry.level}{entry.source === 'dump' ? ' · dump' : ''}</StatusChip> },
    { key: 'category', header: 'Category', cell: (entry) => <Tag>{entry.category}</Tag> },
    { key: 'message', header: 'Message', cell: (entry) => <span className="ui-clip" title={entry.message}>{entry.message}</span> },
  ];
  const copy = async () => {
    const ok = await copyText(logsText(shown));
    setCopied(ok ? { ok: true, text: `Copied ${count(shown.length)} entr${shown.length === 1 ? 'y' : 'ies'}.` } : { ok: false, text: 'The browser did not allow copying.' });
  };
  return (
    <Section title="Log entries">
      {load.state === 'loading' ? <div aria-busy="true"><Skeleton /><Skeleton /></div> : null}
      {missing ? <EmptyState compact icon={FileText} title="No log entries kept for this PC">The service keeps the newest entries of the PCs that sent logs most recently.</EmptyState> : null}
      {load.state === 'failed' && !missing ? <LoadError title="The log entries could not be loaded" error={load.error} words={(error) => fleetFailure(error)} onRetry={reload} retrying={reloading} /> : null}
      {load.state === 'ready' ? (
        <>
          <span className="ui-toolbar">
            <Select size="sm" className="ui-w-auto" aria-label="Category" value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">All</option>
              {logCategoryOptions(entries).map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
            <Button size="sm" icon={Copy} disabled={shown.length === 0} onClick={() => void copy()}>Copy</Button>
            <span className="ui-quiet">{`${count(shown.length)} of ${count(entries.length)} entries, newest first`}</span>
          </span>
          <SaveNote note={copied} />
          <div className="ui-table-box">
            <Table
              stacked
              label="Log entries"
              columns={columns}
              rows={shown}
              rowKey={(entry, index) => `${entry.atMs}|${index}`}
              stickyHeader={false}
              empty={<p className="ui-para ui-quiet">{category && (LOG_CATEGORIES as readonly string[]).includes(category) ? `No ${category} entries kept for this PC.` : 'No entries in this category.'}</p>}
            />
          </div>
        </>
      ) : null}
    </Section>
  );
}
